import { creatorbase } from '@round/api';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { HeaderContext } from '@tanstack/react-table';
import { formatPostStatsCellValue } from 'Modules/Plans/Posts/helpers';
import { BasePostTableMeta, PostsTotalsNumericField } from 'Modules/Plans/Posts/types';
import { PostStatWithAverage } from './PostsStatsWithAverageCell';

type Row = creatorbase.PublicPost;

type Props<T extends Row> = HeaderContext<T, PostStatWithAverage | undefined> & {
    totalsField?: PostsTotalsNumericField;
};

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

/**
 * [!] This component requires a valid accessorFn or accessorKey to be declared on the columnDef.
 */
const PostStatsWithAverageFooterTotalCell = <T extends Row>({ table, column, totalsField }: Props<T>) => {
    const { isLoading, getAreStatsLoading, areApiTotalsEnabled, postsTotals, areTotalsLoading } = getTableMeta(table);

    if (areApiTotalsEnabled) {
        if (areTotalsLoading) {
            return <Skeleton />;
        }

        if (!postsTotals || !totalsField) {
            return '-';
        }

        return formatPostStatsCellValue(postsTotals[totalsField] ?? 0);
    }

    const areStatsLoading = !!table.getRowModel().rows.some((row) => getAreStatsLoading?.(row.original));

    if (isLoading || areStatsLoading) {
        return <Skeleton />;
    }

    const total = table.getRowModel().rows.reduce((acc, row) => {
        const value: PostStatWithAverage['value'] =
            (row.getValue(column.id) as PostStatWithAverage | undefined)?.value || undefined;
        return typeof value === 'number' ? acc + value : acc;
    }, 0);

    return formatPostStatsCellValue(total);
};

export default PostStatsWithAverageFooterTotalCell;
